/**
 * `POST .../replan-preview`: the response schema and the request body the profile editor sends.
 * A leaf module, so a tool outside the React tree builds and parses the same call the editor does.
 *
 * The body is `AssumedAccountIn` (`api/src/socialsrc_api/schemas/estimates.py`), which requires a
 * `rate_per_day` for every enabled lifecycle kind (POST, REEL) and a `comments_per_item`:
 * `_priced_by_assumed_shape` (`routers/tracking.py`) 422s without one, since an assumed shape has
 * no fallback. Both come from the profile listing's own `price.shape`, so the preview and the
 * listing price from one shape.
 */

import { z } from 'zod';

import type { priceSchema } from './profileSchemas';

export const REPLAN_PREVIEW_PATH =
  '/tenants/$tenantId/monitoring-profiles/$profileId/replan-preview';

export const replanPreviewSchema = z.object({
  affected: z.number().int(),
  current_credits_per_day: z.number(),
  projected_credits_per_day: z.number(),
  delta_credits_per_day: z.number(),
  /** The operations with no rate, on either side of the edit. */
  unpriced: z.array(z.string()),
  per_account: z.array(
    z.object({
      id: z.string(),
      handle: z.string().nullable(),
      current: z.number(),
      projected: z.number(),
    }),
  ),
  breakdown: z.object({
    credits_per_day: z.number().nullable(),
    lines: z.array(
      z.object({
        media_kind: z.enum(['POST', 'REEL', 'STORY', 'AUDIO']),
        segment: z.enum(['posts', 'stories', 'comments', 'likers', 'analysis', 'audio']),
        credits_per_day: z.number().nullable(),
        basis: z.enum(['measured', 'worst_case']).nullable(),
        sample_pulls: z.number().int().nonnegative().nullable(),
      }),
    ),
    unpriced: z.array(z.string()),
  }),
  approximate: z.boolean(),
  funded_until: z.string().nullable(),
});

export interface AssumedShape {
  platform: 'instagram' | 'tiktok';
  rate_per_day: Record<'POST' | 'REEL', number>;
  comments_per_item: number;
  tail_comments_per_item: number | null;
  reels_in_feed: boolean | null;
  night_window: { hours: number; multiplier: number } | null;
}

/** Both lifecycle kinds priced at the profile listing's own standard shape, so the preview
 *  never 422s on a kind the page did not name and prices from the figure the listing's own
 *  `price` column already shows. */
export function assumedShapeOf(price: {
  shape: z.infer<typeof priceSchema>['shape'];
}): AssumedShape {
  return {
    platform: 'instagram',
    rate_per_day: { POST: price.shape.rate_per_day, REEL: price.shape.rate_per_day },
    comments_per_item: price.shape.comments_per_item,
    tail_comments_per_item: null,
    reels_in_feed: null,
    night_window: null,
  };
}

interface WireAudio {
  enabled: boolean;
  periods: unknown[];
  page_limit: number | null;
}

interface WireMediaRow {
  media_kind: string;
  enabled: string;
  periods: unknown[];
  story_interval_seconds: number | null;
  comments_enabled: boolean;
  likers_enabled: boolean;
  comment_cap_per_post: number | null;
}

export function audioForWire(audio: WireAudio): unknown {
  return { enabled: audio.enabled, periods: audio.periods, page_limit: audio.page_limit };
}

export function mediaForWire(rows: readonly WireMediaRow[]): unknown[] {
  return rows.map((row) => ({
    media_kind: row.media_kind,
    enabled: row.enabled,
    periods: row.media_kind === 'STORY' ? [] : row.periods,
    story_interval_seconds: row.media_kind === 'STORY' ? row.story_interval_seconds : null,
    comments_enabled: row.comments_enabled,
    likers_enabled: row.likers_enabled,
    comment_cap_per_post: row.comment_cap_per_post,
  }));
}

/** The body for a loaded profile exactly as the editor sends it before any edit. */
export function replanPreviewBody(profile: {
  price: { shape: z.infer<typeof priceSchema>['shape'] };
  media: readonly WireMediaRow[];
  audio: WireAudio;
}): AssumedShape & { media: unknown[]; audio: unknown } {
  return {
    ...assumedShapeOf(profile.price),
    media: mediaForWire(profile.media),
    audio: audioForWire(profile.audio),
  };
}
